/**
 * Ollama API Client
 * Handles communication with local Ollama instance
 */

import type {
  OllamaModel,
  OllamaChatRequest,
  OllamaChatResponse,
  Message,
} from '../types/conversation';

const DEFAULT_BASE_URL = 'http://localhost:11434';

export class OllamaClient {
  private baseUrl: string;

  constructor(baseUrl: string = DEFAULT_BASE_URL) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url.replace(/\/$/, '');
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async listModels(): Promise<OllamaModel[]> {
    const response = await fetch(`${this.baseUrl}/api/tags`);
    if (!response.ok) {
      throw new Error(`Failed to list models: ${response.statusText}`);
    }
    const data = await response.json();
    return data.models || [];
  }

  async chat(
    request: OllamaChatRequest,
    onToken?: (token: string) => void,
    signal?: AbortSignal
  ): Promise<OllamaChatResponse> {
    const response = await fetch(`${this.baseUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...request,
        stream: !!onToken,
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.statusText}`);
    }

    if (onToken && response.body) {
      return this.handleStreamResponse(response.body, onToken);
    }

    return response.json();
  }

  private async handleStreamResponse(
    body: ReadableStream<Uint8Array>,
    onToken: (token: string) => void
  ): Promise<OllamaChatResponse> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    let lastResponse: OllamaChatResponse | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(Boolean);

        for (const line of lines) {
          try {
            const parsed: OllamaChatResponse = JSON.parse(line);
            if (parsed.message?.content) {
              fullContent += parsed.message.content;
              onToken(parsed.message.content);
            }
            if (parsed.done) {
              lastResponse = {
                ...parsed,
                message: { role: 'assistant', content: fullContent },
              };
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return (
      lastResponse || {
        model: '',
        created_at: new Date().toISOString(),
        message: { role: 'assistant', content: fullContent },
        done: true,
      }
    );
  }

  buildChatMessages(
    messages: Message[],
    systemPrompt?: string
  ): OllamaChatRequest['messages'] {
    const chatMessages: OllamaChatRequest['messages'] = [];

    if (systemPrompt) {
      chatMessages.push({ role: 'system', content: systemPrompt });
    }

    for (const msg of messages) {
      chatMessages.push({
        role: msg.role,
        content: msg.content,
      });
    }

    return chatMessages;
  }
}

export const ollamaClient = new OllamaClient();
